// OrderRow.jsx
import React, { useEffect } from "react";
import styled, { css, keyframes } from "styled-components";
import Modal from "../../interface/Modal";
import { BsHourglassSplit } from "react-icons/bs";
import { MdBlock, MdOutlineAccessTime } from "react-icons/md";
import MaintenanceOrderDetail from "../maintenance/MaintenanceOrderDetail";
import { useGetUserDetails } from "../authentication/useGetUserDetails";
import { useRef } from "react";
import { useState } from "react";

const bounce = keyframes`
  0%, 100% {
    background-position: left bottom;
  }
  50% {
    background-position: right top;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  border-radius: 10px;
  padding: 1rem;
  box-shadow: var(--shadow-div-md);
  font-size: clamp(0.8rem, 2vw, 1.4rem);
  background-color: var(--color-grey-50);
  z-index: 1; // Ensure it's above the rotating border
  /* Add other styles you need */
`;

const AssignedPersonnelOverlay = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 7.5rem;
  display: flex;
  gap: 0.2rem;
  z-index: 2;
`;

const StyledResources = styled.div`
  display: flex;
  flex-direction: column;
  color: var(--color-grey-500);
  font-size: 0.6em;
  white-space: nowrap;
  margin-bottom: 1em;
  align-items: center;
`;

const StyledUserImage = styled.img`
  display: flex;
  align-self: center;
  max-width: 4rem; /* Adjust as needed */
  max-height: 4rem;
  border-radius: 50%;
  margin-right: 0.1rem; /* Gives some space between the image and the text */
`;

const ExtraCountCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  border: 2px solid var(--color-grey-100);
  font-size: 0.8em;
  font-weight: bold;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
`;

const ShiftCircle = styled(ExtraCountCircle)`
  background-color: var(--color-grey-100);
  border-color: var(--color-grey-300);
`;

const SplitCircle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  border: 2px solid var(--color-grey-100);
  font-size: 0.8em;
  font-weight: bold;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  overflow: hidden;
`;

const SplitTop = styled.div`
  background-color: var(--color-primary);
  width: 100%;
  height: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SplitBottom = styled.div`
  background-color: var(--color-grey-500);
  width: 100%;
  height: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 1rem;
  max-height: auto;
  padding: 0.1rem;
  background-color: ${(props) => getStateColor(props.$state)};
  box-shadow: var(--shadow-div-md);
  border-radius: 0.1rem;
  margin-bottom: 1.5rem;
  text-overflow: unset;
  align-items: flex-start;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: inherit;
    ${({ $animate, color, $duration, $playstate }) =>
      $animate &&
      css`
        background: linear-gradient(
          to right,
          transparent 45%,
          ${color} 50%,
          transparent 55%
        );
        background-size: 200% 200%;
        animation: ${bounce} ${$duration} linear infinite;
        animation-play-state: ${$playstate};
      `}
  }

  &:hover::before {
    box-shadow: 0 0 10px 5px #ffffff;
  }
`;

const StyledIdent = styled.h5`
  color: var(--color-grey-500);
`;

const StyledAssetGroup = styled.h4`
  color: var(--color-grey-500);
  white-space: nowrap;
`;
const getStateColor = (state) => {
  switch (state) {
    case "Completed":
      return "#337d33";
    case "Finished":
      return "#00cc99";
    case "In Process":
      return "#95de75";
    case "Released":
      return "#fbec9b";
    case "Planned":
      return "#a4bfff";

    default:
      return "black";
  }
};

const StyledState = styled.div`
  display: flex;
  gap: 0.4em; // Adjust the gap as needed
`;

const StateCircle = styled.span`
  background-color: ${(props) => getStateColor(props.$state)};
  border-radius: 50%; // Make it a circle
  width: 1em;
  height: 0.8em;
  display: inline-block;
`;
const StyledStatus = styled.div`
  display: flex;
  margin-left: auto;
  align-items: center;
`;

const StateBlock = styled.div`
  color: red;
  display: flex;
  align-items: center;
  margin-left: 1em;
`;
const StateTimeText = styled.h4`
  margin-left: 0.3em;
`;

const PlanningDays = styled.span`
  color: red;
  font-size: 0.8em;
  margin-left: 1em;
`;

const OrderLink = styled.a`
  color: var(
    --color-primary
  ); // Use your theme's primary color or any color you prefer
`;

const StateText = styled.span`
  font-size: 0.8em;
  color: white; // Adjust text color as needed
`;

const DateText = styled.span`
  font-size: 0.8em;
  margin-left: 3rem;
  color: white; // Adjust text color as needed
`;

const StyledDescription = styled.h5`
  font-weight: 100;
  color: ${(props) =>
    props.$type === "failed"
      ? "var(--color-grey-600)"
      : "var(--color-grey-800)"};
  margin-top: ${(props) => (props.$type === "failed" ? "1em" : null)};
  overflow-y: auto; /* Enable vertical scrolling */
  max-height: calc(
    1.2em * 5
  ); /* Assuming line-height is 1.2em, adjust as needed */
  line-height: 1.2em; /* Adjust line height as needed */
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Limit the content to 5 lines */
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
`;

const AssetGroupTitle = ({ text }) => {
  let parts = text.split(",").map((part) => part.trim()); // Split and trim spaces
  let firstPart = parts[0]; // First meaningful entry
  let additionalCount = parts.length - 1; // Base count of extra items

  // Check if the last part contains "(+X)" and extract the number
  const lastPart = parts[parts.length - 1];
  const match = lastPart.match(/\(\+(\d+)\)/);

  if (match) {
    additionalCount += parseInt(match[1], 10) - 1; // Use the given count but exclude the last counted entry
  }

  return (
    <StyledAssetGroup>
      {additionalCount > 0 ? `${firstPart} (+${additionalCount})` : firstPart}
    </StyledAssetGroup>
  );
};

const convertMinutesToRoundedHours = (minutes) => {
  const hours = minutes / 60;
  const roundedHours = Math.ceil(hours * 2) / 2; // Round up to nearest half-hour
  return roundedHours.toFixed(1); // Display with one decimal
};

const StatusMaintenanceOrders = ({ item, userImages }) => {
  // Example logic to determine color, speed, and whether to $animate based on item.state
  //const color = item.state === "In Process" ? "#dedc75" : "#fb9e9b"; // Customer viewable
  const color = "var(--color-grey-0)"; // Customer viewable
  const $duration = item.state === "In Process" ? "12s" : "20s"; // Example logic
  const $animate = item.state === "In Process" || item.state === "Released"; // Example logic to control animation
  const partsAfterDash = item.orderIdent.split("-")[1] || "";
  const orderId = partsAfterDash.replace(/^0+/, "");
  const blockReq =
    item.assetGroupString.includes("_BLOCK REQUIRED") ||
    item.assignedResources.includes("_BlockRequired");
  // const assignedNames = item.assignedResources
  //   ?.split("•")
  //   .map((n) => n.trim())
  //   .filter(Boolean)
  //   .slice(0, 2);

  function extractFirstName(wiToolName) {
    // Split the string into parts using the comma as a separator
    const parts = wiToolName.split(",");
    // The first name should be after the comma, which is the second element in the array
    // Trim it to remove any leading or trailing whitespace
    const firstName = parts[1]?.trim();
    return firstName || ""; // If there's no comma or no first name, return an empty string
  }

  function renderPerson(name) {
    const image = userImages?.[name];
    return (
      <StyledResources key={name}>
        <StyledUserImage
          src={image || "/images/DefaultUser.png"}
          alt={`${extractFirstName(name)} image`}
        />
      </StyledResources>
    );
  }

  function formatDateEUShort(dateInput) {
  if (!dateInput) return "";

  const d = new Date(dateInput);
  if (isNaN(d)) return "";

  const pad = (n) => String(n).padStart(2, "0");

  const day = pad(d.getDate());
  const month = pad(d.getMonth() + 1);
  const year = String(d.getFullYear()).slice(-2);
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());

  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

  const allNames =
    item.assignedResources
      ?.split("•")
      .map((n) => n.trim())
      .filter(Boolean) || [];

  const shiftTag = allNames.find((n) => /^_SHIFT \d Assigned$/.test(n));
  const assignedNames = allNames
    .filter((n) => !/^_SHIFT \d Assigned$/.test(n))
    .slice(0, 2);

  const hiddenCount =
    allNames.length - assignedNames.length - (shiftTag ? 1 : 0);

  return (
    <Modal>
      <Modal.Open opens="orderdetail">
        <RowContainer
          $animate={$animate}
          color={color}
          $duration={$duration}
          $playstate={$animate} // Control play state
          $state={item.state} // Pass down the $type prop if it's being used
        >
          <Content>
            <StyledState>
              <StateCircle $state={item.state} />
              <StateText>{item.state}</StateText>

              <StyledIdent>
                <OrderLink>{item.orderIdent}</OrderLink>
              </StyledIdent>
              <DateText>{formatDateEUShort(item.plannedDate)}</DateText>
              {item.planningInDays < -14 && (
                <PlanningDays>
                  <BsHourglassSplit />
                  {item.planningInDays}
                </PlanningDays>
              )}

              <StyledStatus>
                <MdOutlineAccessTime />
                <StateTimeText>
                  {convertMinutesToRoundedHours(item.durationPerTechnician)}
                </StateTimeText>
                h
                {blockReq && (
                  <StateBlock>
                    <MdBlock />
                  </StateBlock>
                )}
              </StyledStatus>
            </StyledState>

            {item.assetGroupString && (
              <AssetGroupTitle text={item.assetGroupString} />
            )}
            {item.descrOfFailure && (
              <StyledDescription>{item.descrOfFailure}</StyledDescription>
            )}
            {!item.descrOfFailure && (
              <StyledDescription>{item.description}</StyledDescription>
            )}
          </Content>
          <AssignedPersonnelOverlay>
            {assignedNames.map((name) => renderPerson(name))}

            {shiftTag && hiddenCount > 0 ? (
              <SplitCircle title={`${shiftTag} + ${hiddenCount} more`}>
                <SplitTop>
                  {shiftTag.match(/_SHIFT (\d)/)?.[1]
                    ? `S${shiftTag.match(/_SHIFT (\d)/)[1]}`
                    : "?"}
                </SplitTop>
                <SplitBottom>+{hiddenCount}</SplitBottom>
              </SplitCircle>
            ) : shiftTag ? (
              <ShiftCircle title={shiftTag}>
                {shiftTag.match(/_SHIFT (\d)/)?.[1]
                  ? `S${shiftTag.match(/_SHIFT (\d)/)[1]}`
                  : "?"}
              </ShiftCircle>
            ) : hiddenCount > 0 ? (
              <ExtraCountCircle title={`${hiddenCount} additional`}>
                +{hiddenCount}
              </ExtraCountCircle>
            ) : null}
          </AssignedPersonnelOverlay>
        </RowContainer>
      </Modal.Open>
      <Modal.Window name="orderdetail">
        <MaintenanceOrderDetail item={item} />
      </Modal.Window>
    </Modal>
  );
};

export default StatusMaintenanceOrders;
